<template>
  <v-container fluid class="ma-0 pa-1">
    <v-row no-gutters align="center">
      <v-tooltip top z-index="1000" v-if="isActionVisible('select') && !tableViewEnabled">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="selectChange" class="mx-1">
            <v-icon small>{{ selectIcon }}</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Select") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('workflow')">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="showFlowDiagram" class="mx-1">
            <v-badge v-if="totalValidationsCount > 0" :color="badgeColor(totalValidationsCount)" overlap bordered>
              <template v-slot:badge>
                <span>{{ totalValidationsCount }}</span>
              </template>
              <v-icon small :color="currentUserHasValidatedDocument ? 'green' : ''">mdi-state-machine</v-icon>
            </v-badge>
            <v-icon v-else small>mdi-state-machine</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("FlowChart") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('comment')">
        <template v-slot:activator="{ on }">
          <v-btn @click="actionClick('comment')" icon small v-on="on" class="mx-1">
            <v-badge :color="badgeColor(commentsCount)" overlap bordered>
              <template v-slot:badge>
                <span>{{ commentsCount }}</span>
              </template>
              <v-icon small>mdi-message-reply-text</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>{{ $t("Comments") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('attachment')">
        <template v-slot:activator="{ on }">
          <v-btn @click="actionClick('attachment')" icon small v-on="on" class="mx-1">
            <v-badge :color="badgeColor(attachmentsCount)" overlap bordered>
              <template v-slot:badge>
                <span>{{ attachmentsCount }}</span>
              </template>
              <v-icon small>mdi-attachment</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>{{ $t("DocumentAttachments") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="actionClick('version')" class="mx-1">
            <v-icon small>mdi-book</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Versions") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('history')">
        <template v-slot:activator="{ on }">
          <v-btn @click="actionClick('history')" icon small v-on="on" class="mx-1">
            <v-icon small>mdi-backup-restore</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("History") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn @click="actionClick('alert')" icon small v-on="on" class="mx-1">
            <v-badge :color="badgeColor(alertsCount)" overlap>
              <template v-slot:badge>
                <span>{{ alertsCount }}</span>
              </template>
              <v-icon small>mdi-bell</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>{{ $t("Alerts") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('distribution')">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="actionClick('distribution')" class="mx-1">
            <v-icon small>mdi-share-variant</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("DistributionInternalExternal") }}</span>
      </v-tooltip>

      <v-menu bottom offset-y v-if="isActionVisible('plugins')">
        <template v-slot:activator="{ on: menu, attrs }">
          <v-tooltip top z-index="1000">
            <template v-slot:activator="{ on: tooltip }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="{ ...tooltip, ...menu }"
                @click="loadPluginList()"
                class="mx-1"
              >
                <v-icon small>mdi-power-plug</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Plugins") }}</span>
          </v-tooltip>
        </template>
        <v-list dense>
          <v-list-item
            v-for="plugin in pluginList"
            v-bind:key="plugin.id"
            @click="loadPlugin(plugin)"
          >
            <v-list-item-icon>
              <v-icon small>{{ plugin.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ plugin.name }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn @click="actionClick('linkedDocuments')" icon small v-on="on" class="mx-1">
            <v-badge :color="badgeColor(linkedDocumentsCount)" overlap bordered>
              <template v-slot:badge>
                <span>{{ linkedDocumentsCount }}</span>
              </template>
              <v-icon small>mdi-link-variant</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>{{ $t("Site.LinkedDocuments.Titles.LinkedDocuments") }}</span>
      </v-tooltip>

      <v-spacer />

      <v-tooltip top z-index="1000" v-if="isActionVisible('confirmReception')">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="confirmReception" class="mx-1">
            <v-icon small>mdi-file-eye-outline</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("ConfirmReception") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('unblock')">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="unblockDocument" class="mx-1">
            <v-icon small>mdi-lock-open</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Unblock") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('lockInfo')">
        <template v-slot:activator="{ on }">
          <v-icon small v-on="on" class="mx-1">mdi-lock</v-icon>
        </template>
        <span>{{ lockInfo }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('retryProcess')">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="retryProcess" :loading="loading" class="mx-1">
            <v-icon small>mdi-reload</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("RetryDifferredProcess") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('lockLoading')">
        <template v-slot:activator="{ on }">
          <div class="loader mt-1 mr-1" v-on="on"></div>
        </template>
        <span>{{ $t("WaitingSignatureProcess") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('edit') && !tableViewEnabled">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="editDocument" class="mx-1">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Edit") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('delete') && !tableViewEnabled">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="deleteDocument"
            type="button"
            class="mx-1"
            @keydown.enter.prevent.stop
            @keyup.enter.prevent.stop="deleteDocument">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Delete") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('download')">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="downloadDocument" class="mx-1">
            <v-icon small>mdi-download</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Download") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('print')">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="printDocument" class="mx-1">
            <v-icon small>mdi-printer</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Print") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isActionVisible('sign')">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="signDocument" class="mx-1">
            <v-icon small>mdi-draw</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Sign") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="!tableViewEnabled">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="actionClick('expand')" class="mx-1">
            <v-icon small>{{ expandIcon }}</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Expand") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isDigitallySigned && tableViewEnabled">
        <template v-slot:activator="{ on }">
          <v-icon small v-on="on" class="mx-1">mdi-file-document-edit-outline</v-icon>
        </template>
        <span>{{ $t("DocumentSigned") }}</span>
      </v-tooltip>
    </v-row>
  </v-container>
</template>

<script>
import DocumentService from '@/services/DocumentService';
import PluginService from '@/services/PluginService';
import Plugin from '@/ecm/models/plugin/Plugin';
import Utils from '@/ecm/common/Utils.js';
import AccountType from '@/ecm/variables/AccountType';
import LockedDocumentService from '@/services/LockedDocumentService';

export default {
    name: 'DocumentItemToolbar',
    props: {
        documentId: {
            type: Number,
        },
        commentsCount: {
            type: Number,
            default: 0,
        },
        alertsCount: {
            type: Number,
            default: 0,
        },
        attachmentsCount: {
            type: Number,
            default: 0,
        },
        canUnblock: {
            type: Boolean,
            default: false,
        },
        isBlocked: {
            type: Boolean,
            default: false,
        },
        lockInfo: {
            type: String,
            default: '',
        },
        isDistributed: {
            type: Boolean,
            default: false,
        },
        isPendingConfirmation: {
            type: Boolean,
            default: false,
        },
        permissions: {
            type: Object,
            required: true,
        },
        isLastVersion: {
            type: Boolean,
            default: false,
        },
        isInWorkflow: {
            type: Boolean,
            default: false,
        },
        hasPlugins: {
            type: Boolean,
            default: false,
        },
        isPdf: {
            type: Boolean,
            default: false,
        },
        selectedDocuments: {
            type: Array,
            default: () => [],
        },
        linkedDocumentsCount: {
            type: Number,
            default: 0
        },
        tableViewEnabled: {
            type: Boolean,
            default: false
        },
        isDigitallySigned: {
            type: Boolean,
            default: false
        },
        lockType: {
            type: Number,
            default: 0,
        },
        totalValidationsCount: {
            type: Number,
            default: 0,
        },
        currentUserHasValidatedDocument: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            pluginList: [],
            expandIcon: 'mdi-chevron-down',
            loading: false
        };
    },
    computed: {
        isSelected() {
            return (
                this.selectedDocuments.filter((d) => d == this.documentId).length > 0
            );
        },
        selectIcon() {
            return this.isSelected
                ? 'mdi-check-box-outline'
                : 'mdi-checkbox-blank-outline';
        },
        isReadonlyUser() {
            return (
                this.$store.getters['account/user'].AccountType == AccountType.Readonly
            );
        },
    },
    methods: {
        actionClick(action) {
            this.$emit('on-action-click', action);
            if (action === 'expand') {
                this.expandIcon =
                    this.expandIcon == 'mdi-chevron-down'
                        ? 'mdi-chevron-up'
                        : 'mdi-chevron-down';
            } else {
                this.expandIcon = 'mdi-chevron-down';
            }
        },
        selectChange() {
            this.$emit('on-selection-change', !this.isSelected);
        },
        deleteDocument() {
            const ids = [this.documentId];
            this.$confirm(this.$t('DeleteAllVersionsConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-lock-question',
                color: 'warning',
                width: 400,
                buttonTrueText: this.$t('CurrentVersion'),
                buttonFalseText: this.$t('AllVersions'),
            }).then((res) => {
                if (res > 0) {
                    var deleteAllVersions = res != 1;
                    DocumentService.removeVersion(ids, deleteAllVersions).then(
                        (result) => {
                            Utils.toast.success(this.$t('DocumentsDeletedSuccessfully'));
                            if (deleteAllVersions) {
                                this.$emit('on-document-removed', this.documentId);
                            } else {
                                this.$emit('on-version-removed', result);
                            }
                        }
                    );
                }
            });
        },
        downloadDocument() {
            const id = this.documentId;
            if (this.isReadonlyUser) {
                DocumentService.download(id, false);
                return;
            }

            if (this.isBlocked && !this.canUnblock) {
                DocumentService.download(id, false);
                return;
            }
            this.$confirm(this.$t('LockForEditionConfirmation'), {
                title: this.$t('Download'),
                icon: 'mdi-lock-question',
                color: 'warning',
                width: 400,
                buttonTrueText: this.$t('DownloadAndBlock'),
                buttonFalseText: this.$t('Download'),
            }).then((res) => {
                if (res > 0) {
                    const blockDocument = res == 1;
                    if (blockDocument && !this.permissions.documentEdit) {
                        Utils.toast.error(this.$t('CannotEditAndDownload'));
                        return;
                    } else {
                        DocumentService.download(id, blockDocument);

                        this.canUnblock = true;

                        if (res == 1) this.$emit('on-document-blocked');
                    }
                }
            });
        },
        unblockDocument() {
            LockedDocumentService.unlockOwned(this.documentId).then(() => {
                this.$emit('on-document-blocked');
            });
        },
        editDocument() {
            this.$emit('on-edit-click');
        },
        showFlowDiagram() {
            this.$emit('on-flow-diagram-click');
        },
        signDocument() {
            this.$emit('on-sign-click');
        },
        confirmReception() {
            DocumentService.confirmReception(this.documentId).then((result) => {
                this.$emit('on-reception-confirmed');
            });
        },
        printDocument() {
            DocumentService.print(this.documentId);
        },
        loadPlugin(plugin) {
            this.$emit('on-load-plugin-click', plugin);
        },
        loadPluginList() {
            PluginService.getAuthorizedList('ResultViewDocument', [
                this.documentId,
            ]).then((result) => {
                this.pluginList = result.map(function (dto) {
                    return new Plugin(dto);
                });
            });
        },
        badgeColor(count) {
            return count > 0 ? 'red lighten-1' : 'grey lighten-1';
        },
        isActionVisible(action) {
            const isActionable = !this.isBlocked || (this.isBlocked && this.canUnblock && this.lockType < 2);

            const rules = {
                select: isActionable,
                workflow: this.isInWorkflow,
                comment: this.permissions.commentRead,
                attachment: this.permissions.documentAttachmentRead,
                history: this.permissions.auditRead,
                distribution: this.isDistributed,
                plugins: this.hasPlugins,

                confirmReception: this.isPendingConfirmation,
                unblock: this.canUnblock && this.lockType < 2,
                lockInfo: this.isBlocked && !this.canUnblock && this.lockType < 2,
                lockLoading: this.isBlocked && this.lockType >= 2,

                edit: this.permissions.documentEdit && this.isLastVersion && isActionable,
                delete: this.permissions.documentDelete && isActionable,
                download: this.permissions.documentDownload && isActionable,
                print: this.permissions.documentPrint && isActionable,
                sign: this.permissions.documentSign && this.isPdf && this.isLastVersion && isActionable,
                retryProcess: this.isBlocked && this.lockType >= 2,
            };

            return rules[action] !== undefined ? rules[action] : true;
        },
        retryProcess() {
            this.$emit('on-retry-process');
        }
    },
};
</script>
<style>
.loader {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-block;
  border-top: 2px solid #FFF;
  border-right: 2px solid transparent;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}
.loader::after {
  content: '';
  box-sizing: border-box;
  position: absolute;
  left: 0;
  top: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border-left: 2px solid #008cff;
  border-bottom: 2px solid transparent;
  animation: rotation 0.5s linear infinite reverse;
}
@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
